import React from 'react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { CheckCircle, MessageSquare } from 'lucide-react';

interface AgentComparisonProps {
  agent: {
    type: string;
    name: string;
    description: string;
    icon: React.ComponentType<{ className?: string }>;
    color: string;
    available: boolean;
    capabilities: string[];
    useCases: string[];
  };
  onStartConversation: () => void;
}

export function AgentComparison({ agent, onStartConversation }: AgentComparisonProps) {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className={`w-12 h-12 rounded-lg bg-${agent.color}-100 flex items-center justify-center`}>
            <agent.icon className={`w-6 h-6 text-${agent.color}-600`} />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{agent.name}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">{agent.description}</p>
          </div>
        </div>
        <Button 
          onClick={agent.available ? onStartConversation : undefined}
          disabled={!agent.available}
          className={
            agent.available 
              ? "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              : "bg-gray-300 text-gray-500 cursor-not-allowed"
          }
        >
          <MessageSquare className="w-4 h-4 mr-2" />
          {agent.available ? 'Start Conversation' : 'Coming Soon'}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Capabilities */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">CAPABILITIES</h4>
          <div className="space-y-2">
            {agent.capabilities.map((capability, index) => (
              <div key={index} className="flex items-start">
                <CheckCircle className={`w-4 h-4 text-${agent.color}-600 mr-2 mt-0.5`} />
                <span className="text-sm text-gray-700 dark:text-gray-300">{capability}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Example Use Cases */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">EXAMPLE QUESTIONS</h4>
          <div className="space-y-2">
            {agent.useCases.map((useCase, index) => (
              <div key={index} className="bg-gray-50 dark:bg-gray-700 p-2 rounded-lg">
                <p className="text-sm text-gray-700 dark:text-gray-300">"{useCase}"</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-700">
        <h4 className="font-medium text-blue-900 dark:text-blue-300 mb-2">Pro Tips for Better Results</h4>
        <div className="space-y-1 text-sm text-blue-800 dark:text-blue-300">
          <p>• Be specific about time periods (e.g., "last week", "this month")</p>
          <p>• Ask about specific metrics you're interested in</p>
          <p>• Follow up with clarifying questions for deeper insights</p>
          <p>• Try comparing different time periods or segments</p>
        </div>
      </div>

      <div className="flex space-x-3">
        <Button 
          onClick={agent.available ? onStartConversation : undefined}
          disabled={!agent.available}
          className={
            agent.available 
              ? "flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              : "flex-1 bg-gray-300 text-gray-500 cursor-not-allowed"
          }
        >
          <MessageSquare className="w-4 h-4 mr-2" />
          {agent.available ? 'Start Conversation' : 'Coming Soon'}
        </Button>
        <Button variant="outline" className="flex-1" disabled={!agent.available}>
          Learn More
        </Button>
      </div>
    </div>
  );
}